import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from '@mui/x-data-grid-premium';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useReviewInformation from '@/hooks/useReviewInformation';
import { CountryProps } from '@/models/Country';
import { CountyProps } from '@/models/County';
import { DeleteReviewerRequestProps } from '@/models/DeleteReviewerRequest';
import { ProgramApiResponse } from '@/models/Program';
import { ReviewerAssignmentProps } from '@/models/ReviewerAssignment';
import { ReviewerDashboardProps } from '@/models/ReviewerDashboard';
import { StateProps } from '@/models/State';
import { UserType, userTypeAsString } from '@/models/User';
import { getCountryByCountryId, getCountyByCountyId, getStateByStateId } from '@/utils/addressUtil';
import { dataGridStringCompare, GridLocaleTextOverride } from '@/utils/dataGridUtil';

import ReviewerModal from './AddReviewerAssignmentModal/ReviewerModal';
import { LocaleContext } from '@/Contexts/LocaleContext';
import ConfirmationModal from '@/components/common/ConfirmationModal';

function ReviewerAssignments({
  reviewerAssignmentLookup,
  currentReviewerStateIds,
}: {
  reviewerAssignmentLookup: { [key: string]: ReviewerAssignmentProps[] } | null;
  currentReviewerStateIds: string[] | null;
}) {
  const { deleteReviewerAssignments } = useReviewInformation();
  const theme = useTheme();

  const { allPrograms, allStates, allCounties, allCountries } = useContext(ConfigContext);
  const { userType, usersAll } = useContext(UsersContext);
  const { setAlert } = useContext(SnackbarContext);
  const { locale } = useContext(LocaleContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<UserType>();
  const [reviewers, setReviewers] = useState<ReviewerDashboardProps[] | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<ReviewerDashboardProps | null>(null);

  const openDeleteConfirmation = useCallback((row: ReviewerDashboardProps) => {
    setPendingDeleteRow(row);
    setConfirmDeleteOpen(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(false);
    setPendingDeleteRow(null);
  }, []);

  const confirmDeleteAssignment = useCallback(async () => {
    if (pendingDeleteRow == null) return;
    const { email, programId, countryId, stateId, countyId, rowId } = pendingDeleteRow;
    if (email == null) return;

    const deleteBody: DeleteReviewerRequestProps = {
      email,
      programId: programId != null ? Number(programId) : undefined,
      countryId: countryId != null ? Number(countryId) : undefined,
      stateId: stateId != null ? stateId : undefined,
      countyId: countyId != null ? countyId : undefined,
    };

    try {
      await deleteReviewerAssignments(deleteBody);
      setReviewers((prev) => (prev != null ? prev.filter((r) => r.rowId !== rowId) : prev));
    } catch (err) {
      console.error('Failed to delete reviewer assignment', err);
      setAlert({
        severity: 'error',
        message: 'Failed to delete reviewer assignment.',
      });
    }
  }, [deleteReviewerAssignments, pendingDeleteRow, setAlert]);

  useEffect(() => {
    if (usersAll != null && reviewerAssignmentLookup != null) {
      const tmpRevRows: ReviewerDashboardProps[] = [];

      Object.keys(reviewerAssignmentLookup).forEach((email: string) => {
        reviewerAssignmentLookup[email].forEach((ra: ReviewerAssignmentProps, raIndex: number) => {
          const row: ReviewerDashboardProps = {
            rowId: `${email}-${ra.programId}-${ra.countryId}-${ra.stateId}-${ra.countyId}-${raIndex}`,
            email,
            validated: ra.user?.validated ?? false,
            firstName: ra.user?.contact?.firstName,
            lastName: ra.user?.contact?.lastName,
            userType: ra.user?.userType,
            disabled: ra.user?.disabled ?? false,
            programId: ra.programId,
            countryId: ra.countryId,
            countyId: ra.countyId,
            stateId: ra.stateId,
          };

          tmpRevRows.push(row);
        });
      });

      if (userType === UserType.Adf) {
        setReviewers(tmpRevRows);
        return;
      }

      if (
        userType === UserType.StateCoordinator &&
        currentReviewerStateIds != null &&
        currentReviewerStateIds.length > 0
      ) {
        const filtered = tmpRevRows.filter(
          (x) =>
            x.stateId != null &&
            (x.userType === UserType.RegionalCoordinator ||
              x.userType === UserType.StateCoordinator) &&
            currentReviewerStateIds.includes(x.stateId)
        );
        setReviewers(filtered);
      }
    }
  }, [currentReviewerStateIds, reviewerAssignmentLookup, userType, usersAll]);

  const getFlagLabel = (row: ReviewerDashboardProps) => {
    if (!row?.validated) return 'Account Pending';
    if (row.disabled) return 'Inactive';
    return 'Active';
  };

  const getReviewerDashboardStateName = useCallback(
    (row: ReviewerDashboardProps) => {
      if (!row.stateId || !allStates?.length) return '';
      const state: StateProps | null = getStateByStateId(allStates, row.stateId);
      return state?.abbrev ?? '';
    },
    [allStates]
  );

  const getReviewerDashboardCountyName = useCallback(
    (row: ReviewerDashboardProps) => {
      if (!row.countyId || !row.stateId || !allCounties.length) return '';
      const county: CountyProps | null = getCountyByCountyId(
        allCounties,
        row.countyId,
        row.stateId
      );
      return county?.name ?? '';
    },
    [allCounties]
  );

  const getReviewerDashboardCountryName = useCallback(
    (row: ReviewerDashboardProps) => {
      if (!row.countryId || !allCountries.length) return '';
      const country: CountryProps | null = getCountryByCountryId(allCountries, row.countryId);
      return country?.name ?? '';
    },
    [allCountries]
  );

  const reviewerCols: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        valueGetter: (_, row) => getFlagLabel(row),
        renderCell: (params) => <Chip label={getFlagLabel(params.row)} />,
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        width: 200,
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        width: 200,
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 300,
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'userType',
        headerName: 'Reviewer Type',
        width: 300,
        valueGetter: (_, row) => (row.userType ? userTypeAsString(row.userType) : ''),
        renderCell: (params) =>
          params.row.userType ? userTypeAsString(params.row.userType) : <></>,
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'programId',
        headerName: 'Program',
        width: 250,
        renderCell: (params) =>
          allPrograms?.find((p: ProgramApiResponse) => p.id === params.row.programId)
            ?.programName ?? '',
        valueGetter: (_, row) =>
          allPrograms?.find((p: ProgramApiResponse) => p.id === row.programId)?.programName ?? '',
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'stateAbbrev',
        headerName: 'State',
        renderCell: (params) => getReviewerDashboardStateName(params.row),
        valueGetter: (_, row) => getReviewerDashboardStateName(row),
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'countyName',
        headerName: 'County',
        width: 200,
        renderCell: (params) => getReviewerDashboardCountyName(params.row),
        valueGetter: (_, row) => getReviewerDashboardCountyName(row),
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'countryName',
        headerName: 'Country',
        width: 200,
        renderCell: (params) => getReviewerDashboardCountryName(params.row),
        valueGetter: (_, row) => getReviewerDashboardCountryName(row),
        sortComparator: dataGridStringCompare,
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ReviewerDashboardProps;

          return (
            <Tooltip title='Remove Assignment from Reviewer'>
              <IconButton onClick={() => openDeleteConfirmation(row)}>
                <DeleteIcon sx={{ color: theme.palette.taiga.main }} />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [
      allPrograms,
      getReviewerDashboardCountryName,
      getReviewerDashboardCountyName,
      getReviewerDashboardStateName,
      openDeleteConfirmation,
      theme.palette.taiga.main,
    ]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '70vh',
      }}
    >
      <Box
        width='100%'
        display='flex'
        flex='1 0 auto'
        justifyContent='space-around'
        margin='0px 0px 30px 0px'
      >
        <Button
          sx={{ mt: 2 }}
          variant='secondary'
          size='small'
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => {
            setModalType(UserType.RegionalCoordinator);
            setModalOpen(true);
          }}
        >
          Add Regional Coordinator Assignment
        </Button>

        {userType === UserType.Adf && (
          <>
            <Button
              sx={{ mt: 2 }}
              variant='secondary'
              size='small'
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                setModalType(UserType.StateCoordinator);
                setModalOpen(true);
              }}
            >
              Add State Coordinator Assignment
            </Button>

            <Button
              sx={{ mt: 2 }}
              variant='secondary'
              size='small'
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                setModalType(UserType.NationalProgrammeLead);
                setModalOpen(true);
              }}
            >
              Add National Programme Lead Assignment
            </Button>
          </>
        )}
      </Box>

      {/* ✅ Shared modal */}
      <ReviewerModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        type={modalType}
        states={
          userType === UserType.Adf && allStates != null
            ? allStates
            : currentReviewerStateIds != null && currentReviewerStateIds.length > 0
              ? allStates?.filter(
                  (x: StateProps) => x.id != null && currentReviewerStateIds.includes(x.id)
                )
              : []
        }
      />

      <ConfirmationModal
        open={confirmDeleteOpen}
        onClose={closeDeleteConfirmation}
        header='Delete Reviewer Assignment'
        confirmationText='Are you sure you want to remove this reviewer assignment? This action cannot be undone.'
        confirmButtonText='Remove Assignment'
        onDelete={confirmDeleteAssignment}
      />

      <DataGridPremium
        showToolbar
        localeText={GridLocaleTextOverride(locale)}
        getRowId={(row: ReviewerDashboardProps) => row.rowId}
        sx={{ mx: 1, flex: '1 1 auto' }}
        rows={reviewers ?? []}
        columns={reviewerCols}
        initialState={{ density: 'compact' }}
        disableDensitySelector
        autoPageSize
        pagination
        slots={{
          noRowsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              <div>
                <span>Loading Reviewers...</span>
                {reviewers != null && (
                  <span style={{ display: 'inline-block', marginLeft: 10 }}>
                    <CircularProgress size='16px' />
                  </span>
                )}
              </div>
            </Stack>
          ),
          noResultsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              <div>No Reviewers</div>
            </Stack>
          ),
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
      />
    </Box>
  );
}

export default ReviewerAssignments;
